// cSpell: ignore vendormanager
import { inject, NgModule } from "@angular/core";
import { ActivatedRoute, ActivatedRouteSnapshot, GuardResult, MaybeAsync, ParamMap, Router, RouterModule, RouterStateSnapshot, Routes, UrlTree } from "@angular/router";
import { getLoggerFor } from "@tylertech/logging";
import { map } from "rxjs/operators";
import * as components from "./components";
import { SecurityScreenType } from "./components";
import * as services from "./services";
import { SecurityRoute, SecuritySearchQueryKey } from "./services";

const routingLogger = getLoggerFor("CoreRoutingModule");

/**
 * Requires that the there is no user logged in currently
 * @param childRoute
 * @param _state
 * @returns
 */
export function requireNotLoggedIn(childRoute: ActivatedRouteSnapshot, routerState: RouterStateSnapshot): MaybeAsync<GuardResult>
{
    const securityService = inject(services.SecurityService);
    const router = inject(Router);

    routingLogger.debug(`requireNotLoggedIn(${childRoute}, routerState)`, { childRoute, routerState });
    if (securityService.isAuthenticated)
        return router.createUrlTree(["/"]);
    return true;
}

/** Requires that the system is configured to use TylerId */
export function requireTylerId(childRoute: ActivatedRouteSnapshot, routerState: RouterStateSnapshot): MaybeAsync<GuardResult>
{
    const systemInfoService = inject(services.SystemInfoService);
    routingLogger.debug(`requireTylerId(${childRoute}, routerState)`, { childRoute, routerState });
    return systemInfoService.getSystemInfo()
        .pipe(
            map(x =>
            {
                return x.useTylerId;
            }));
}

/**
 * Will force the security service to clear tokens and log out the current user, if there is one.
 * @param childRoute
 * @param routerState
 * @returns
 */
export function forceLoggedOut(childRoute: ActivatedRouteSnapshot, routerState: RouterStateSnapshot): MaybeAsync<GuardResult>
{
    const securityService = inject(services.SecurityService);

    routingLogger.debug(`forceLoggedOut(${childRoute}, routerState)`, { childRoute, routerState });
    if (securityService.isAuthenticated)
    {
        securityService.clearToken();
    }

    return true;
}

/**
 * Should determine if a security route is used and removes it.
 * ! NOTE: ALL CHILD ROUTES MUST USE THIS FUNCTION IN THEIR CanActivate setup.
 *
 * @param _childRoute
 * @param _state
 * @returns
 */
export function requireLoggedIn(childRoute: ActivatedRouteSnapshot, routerState: RouterStateSnapshot): MaybeAsync<GuardResult>
{
    const router = inject(Router);
    const securityService = inject(services.SecurityService);
    const navigationService = inject(services.NavigationService);

    routingLogger.debug(`requireLoggedIn(${childRoute}, routerState)`, { childRoute, routerState });

    if (!securityService.principle)
    {
        const loginUrlTree = buildLoginRoute();
        return loginUrlTree;
    }

    if (securityService.isAuthenticated)
    {
        if (securityService.isVendorUser
            && !navigationService.isApp("vendormanager"))
        {
            navigationService.navigateToApp("vendormanager");
        }

        if (childRoute.queryParams && childRoute.queryParams.K)
        {
            const currentUrlTree = router.parseUrl(routerState.url);
            delete currentUrlTree.queryParams[SecuritySearchQueryKey.AuthenticationKey];

            return currentUrlTree;
        }

        return true;
    }

    routingLogger.warning(`Access denied! Redirecting`);

    const loginUrlTree = buildLoginRoute();
    return loginUrlTree;

    function buildLoginRoute(): UrlTree
    {
        let url = `/${SecurityRoute.Login}`;

        url += getParams();

        return router.parseUrl(url);
    }

    function getParams(): string
    {
        const redirectParams = securityService.buildRedirectParams(childRoute);

        if (redirectParams)
        {
            const params = [];
            if (redirectParams.redirectPath)
            {
                params.push(`redirectPath=${encodeURIComponent(redirectParams.redirectPath)}`);
            }

            if (redirectParams.redirectParams)
            {
                params.push(`redirectParams=${encodeURIComponent(redirectParams.redirectParams)}`);
            }

            if (redirectParams.K)
            {
                params.push(`K=${redirectParams.K}`);
            }

            return params.length > 0 ? '?' + params.join('&') : "";
        }

        return "";
    }
}

const routes: Routes = [
    {
        path: SecurityRoute.Radius,
        title: "Login",
        component: components.SecurityScreenComponent,
        data: {
            bypassTylerId: true,
            formType: SecurityScreenType.Login,
        },
        canActivate: [requireNotLoggedIn],
    },
    {
        path: "native",
        redirectTo: "/login",
        pathMatch: "full"
    },
    {
        path: SecurityRoute.Login,
        title: "Login",
        component: components.SecurityScreenComponent,
        data: {
            formType: SecurityScreenType.Login,
        },
        canActivate: [requireNotLoggedIn],
    },
    {
        path: SecurityRoute.PopupLoginCallback,
        title: "Login Callback",
        component: components.TidLoginCallbackComponent,
    },
    {
        path: SecurityRoute.Signout,
        title: "Logout",
        component: components.SecurityScreenComponent,
        data: {
            formType: SecurityScreenType.Signout,
        },
        canActivate: [forceLoggedOut],
    },
    {
        path: SecurityRoute.ChangePassword,
        title: "Change Password",
        component: components.SecurityScreenComponent,
        data: {
            formType: SecurityScreenType.ChangePassword,
        },
        //  ! FIXME(DO-4549) - should the ChangePassword route require the user to be already logged in?
        canActivate: [requireLoggedIn],
    },
    {
        path: SecurityRoute.PasswordReset,
        title: "Reset Password",
        component: components.SecurityScreenComponent,
        data: {
            formType: SecurityScreenType.ResetPassword,
        },
        //  ! FIXME(DO-4549) - should the ResetPassword route require the user to be already logged in?
        // canActivate: [requireLoggedIn],
    },
    {
        path: `${SecurityRoute.PasswordReset}?key=:key`,
        title: "Reset Password",
        component: components.SecurityScreenComponent,
        data: {
            formType: SecurityScreenType.ResetPassword,
        },
        //  ! FIXME(DO-4549) - should the ResetPassword route require the user to be already logged in?
        // canActivate: [requireNotLoggedIn],
    },
    {
        path: SecurityRoute.MigrateTid,
        title: "Migrate to TylerID",
        component: components.SecurityScreenComponent,
        data: {
            formType: SecurityScreenType.MigrateTidUser,
        },
        canActivate: [
            requireNotLoggedIn,
            requireTylerId,
        ],
    },
    {
        path: "escalation-chat",
        title: "Support Escalation",
        component: components.EscalationChatComponent,
    }
];
@NgModule({
    imports: [
        RouterModule.forRoot(
            routes,
            {
                enableTracing: routingLogger.enabled,
                // urlUpdateStrategy: "eager",
                // onSameUrlNavigation: "reload"
            }),
    ],
    exports: [RouterModule],
})
export class CoreRoutingModule
{
    private readonly log = getLoggerFor("CoreRoutingModule");

    constructor(
        activatedRoute: ActivatedRoute,
    )
    {
        if (!this.log.enabled)
        {
            return;
        }

        activatedRoute.paramMap.subscribe((paramMap: ParamMap) =>
        {
            const params = paramMap.keys.reduce(
                (a, k) =>
                {
                    a[k] = paramMap.get(k);

                    return a;
                },
                {});

            this.log.debug(`ActivatedRoute.ParamMapChange`, params);
        });

        activatedRoute.queryParamMap.subscribe((paramMap: ParamMap) =>
        {
            const params = paramMap.keys.reduce(
                (a, k) =>
                {
                    a[k] = paramMap.get(k);

                    return a;
                },
                {});

            this.log.debug(`ActivatedRoute.QueryParamMapChange`, params);
        });
    }
}

